import { useMemo, useState } from 'react';

const stateLabel = {
  ok: 'Operational',
  issue: 'Issue',
  down: 'Down',
  empty: 'No data',
};

const tipText = (cell) => {
  if (!cell) return '';
  if (cell.state === 'empty') return `${cell.title} · No data`;
  if (cell.state === 'ok') return `${cell.title} · Operational`;
  if (cell.state === 'down') {
    return `${cell.title} · Down · ${cell.downtimeLabel}${cell.incidents ? ` · ${cell.incidents} time(s)` : ''}`;
  }
  return `${cell.title} · Issue · ${cell.downtimeLabel}${cell.incidents ? ` · ${cell.incidents} time(s)` : ''}`;
};

export default function StatusBoard({ data }) {
  const [tip, setTip] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sortBy, setSortBy] = useState('percent');

  const monitors = useMemo(() => {
    const rows = [...(data?.monitors || [])];
    if (sortBy === 'id') {
      rows.sort((a, b) =>
        String(a.deviceCode || a.deviceId).localeCompare(String(b.deviceCode || b.deviceId), undefined, {
          numeric: true,
        })
      );
    } else {
      rows.sort((a, b) => (Number(b.score) || -1) - (Number(a.score) || -1));
    }
    return rows;
  }, [data, sortBy]);

  if (!data) return null;

  return (
    <div className="status-board">
      <div className={`status-hero ${data.headlineState}`}>
        <div className={`status-hero-dot ${data.headlineState}`} />
        <div>
          <div className="status-hero-title">{data.headline}</div>
          <div className="muted">Last updated {data.updatedAtLabel}</div>
        </div>
      </div>

      <div className="status-overall">
        {(data.overall || []).map((item) => (
          <article key={item.key} className="status-overall-card">
            <div className="stat-kicker">{item.label}</div>
            <div className="stat-value">{item.scoreLabel}</div>
          </article>
        ))}
      </div>

      <div className="status-section-head">
        <strong>Uptime last {data.days} days</strong>
        <div className="status-sort">
          <span className="muted">{data.period?.label}</span>
          <div className="tabs">
            <button
              type="button"
              className={`tab${sortBy === 'percent' ? ' active' : ''}`}
              onClick={() => setSortBy('percent')}
            >
              Percentage
            </button>
            <button
              type="button"
              className={`tab${sortBy === 'id' ? ' active' : ''}`}
              onClick={() => setSortBy('id')}
            >
              Camera ID
            </button>
          </div>
        </div>
      </div>

      <div className="status-list">
        {monitors.map((monitor) => (
          <div key={monitor.deviceId} className="status-row">
            <div className="status-name">
              <span className={`dot ${monitor.status.toLowerCase()}`} />
              <div>
                <strong>{monitor.deviceName}</strong>
                <small className="muted">{monitor.stillOffline ? 'Down now' : 'Operational'}</small>
              </div>
            </div>
            <div className="status-bars" onMouseLeave={() => setTip(null)}>
              {(monitor.timeline || []).map((cell) => (
                <button
                  key={cell.key}
                  type="button"
                  className={`status-bar ${cell.state}`}
                  title={tipText(cell)}
                  aria-label={tipText(cell)}
                  onMouseEnter={(event) =>
                    setTip({
                      text: `${monitor.deviceName} · ${tipText(cell)}`,
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                  onClick={() => setDetail({ monitor, cell })}
                />
              ))}
            </div>
            <div className={`status-score${monitor.stillOffline ? ' down' : ''}`}>
              {monitor.scoreLabel}
            </div>
          </div>
        ))}
      </div>

      {tip && !detail && (
        <div className="status-tip" style={{ left: tip.x + 12, top: tip.y + 16 }}>
          {tip.text}
        </div>
      )}

      {detail && (
        <div className="status-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="status-modal" onClick={(event) => event.stopPropagation()}>
            <div className="status-modal-head">
              <div>
                <div className="stat-kicker">{stateLabel[detail.cell.state] || detail.cell.state}</div>
                <h2>{detail.monitor.deviceName}</h2>
                <p className="muted">{detail.cell.title}</p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setDetail(null)} aria-label="Close">
                ×
              </button>
            </div>

            <div className="status-modal-summary">
              <div>
                <span className="muted">Total downtime</span>
                <strong>{detail.cell.downtimeLabel || '0s'}</strong>
              </div>
              <div>
                <span className="muted">Times offline</span>
                <strong>{detail.cell.incidents ?? 0}</strong>
              </div>
              <div>
                <span className="muted">Score</span>
                <strong>{detail.cell.score == null ? '—' : `${Number(detail.cell.score).toFixed(1)}%`}</strong>
              </div>
            </div>

            {!detail.cell.events?.length ? (
              <p className="muted">
                {detail.cell.state === 'ok'
                  ? 'No downtime on this day.'
                  : 'No detailed events for this day.'}
              </p>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>TIME OFF</th>
                    <th>TIME ONLINE BACK</th>
                    <th>HOW LONG</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.cell.events.map((event, index) => (
                    <tr key={`${event.timeOff}-${index}`}>
                      <td className="time-off">{event.timeOffLabel || '—'}</td>
                      <td className={event.ongoing ? 'time-off' : 'time-on'}>
                        {event.timeOnlineBackLabel || '—'}
                      </td>
                      <td>{event.durationLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
